import {
  getCities,
  getCityFlagSvgUrl,
  getCountries,
  getCountryShieldSvgUrl,
} from '../../api/fetchDataFromCDN';
import { getCountryName } from '../../utils/countryName';

export type ModeKey = 'city-flags' | 'emblems';

/**
 * One answerable pairing: a label on the left, an image to drag to it.
 *
 * Field names date from City Flags, which shipped first. For Emblems `name` is
 * the country, `country` is unused, and `flag` holds the emblem artwork.
 */
export type MatchItem = {
  /** Stable identity — city index, or country code. Also the share link id. */
  id: string;
  name: string;
  /** Muted second line. Empty when the mode has nothing to put there. */
  country: string;
  flag: string;
};

export type MatchModeConfig = {
  key: ModeKey;
  path: string;
  /** i18n namespace holding this mode's strings, e.g. `modes.city-flags`. */
  i18nBase: string;
  /** Fragment prefix for shared links: `#img-420`, `#emb-gb`. */
  sharePrefix: string;
  /**
   * Shape of the drop zone and the tile, which are always identical to each
   * other. Flags are landscape; emblems are portrait and need the height.
   */
  boxAspect: string;
  /**
   * Let the label wrap instead of truncating. Emblems are answered purely from
   * the country name, so a clipped "British Virgin Isl…" makes the row
   * unplayable; City Flags has a second line to fall back on.
   */
  labelWraps: boolean;
  loadItems: (language: string) => Promise<MatchItem[]>;
  /** Rebuilds an item's image straight from its id, for shared links. */
  imageUrlForId: (id: string) => string;
};

/**
 * Restrict City Flags to capital cities. Off by default, so the pool is all
 * ~330 cities that have a flag rather than only the ~153 capitals.
 */
export const CAPITAL_CITIES_ONLY = false;

/**
 * The CDN has no shield artwork for these, so they can never be a question or
 * a distractor. `testImageLoad` is the runtime net for anything else that goes
 * missing later — this list only saves it a wasted round trip.
 */
export const COUNTRIES_WITHOUT_EMBLEM = ['AQ', 'SJ'];

export const CITY_FLAGS: MatchModeConfig = {
  key: 'city-flags',
  path: '/city-flags',
  i18nBase: 'modes.city-flags',
  sharePrefix: 'img',
  boxAspect: 'aspect-[4/3]',
  labelWraps: false,
  loadItems: async (language) => {
    // getCities localises `name` in place for the language it is given.
    const [cities, countries] = await Promise.all([
      getCities(language),
      getCountries(language),
    ]);

    const countryByCode = new Map(
      countries.map((country) => [
        country.code.toUpperCase(),
        getCountryName(language, country),
      ]),
    );

    return cities
      .filter((city) => city.flag && (!CAPITAL_CITIES_ONLY || city.capital))
      .map((city) => ({
        id: String(city.index),
        name: city.name,
        country: countryByCode.get(city.countryCode.toUpperCase()) ?? '',
        flag: getCityFlagSvgUrl(city.flag as string),
      }));
  },
  // City flag files are named by their index.
  imageUrlForId: (id) => getCityFlagSvgUrl(`/data/common/city-flags/${id}.svg`),
};

export const EMBLEMS: MatchModeConfig = {
  key: 'emblems',
  path: '/emblems',
  i18nBase: 'modes.emblems',
  sharePrefix: 'emb',
  boxAspect: 'aspect-square',
  labelWraps: true,
  loadItems: async (language) => {
    const countries = await getCountries(language);

    // BANNED_COUNTRIES is intentionally NOT applied: it is a daily-only rule,
    // and these modes quiz the full world.
    //
    // The artwork is not probed here on purpose. Checking all ~247 shields up
    // front would queue hundreds of requests behind the browser's per-origin
    // limit and stall the first round; the exclusion list above covers the two
    // countries the CDN actually lacks.
    return countries
      .filter(
        (country) =>
          !COUNTRIES_WITHOUT_EMBLEM.includes(country.code.toUpperCase()),
      )
      .map((country) => ({
        id: country.code.toLowerCase(),
        name: getCountryName(language, country),
        country: '',
        flag: getCountryShieldSvgUrl(country.code),
      }));
  },
  imageUrlForId: (id) => getCountryShieldSvgUrl(id),
};

export const MODE_LINKS: Record<ModeKey, { path: string; titleKey: string }> = {
  'city-flags': { path: '/city-flags', titleKey: 'modes.city-flags.title' },
  emblems: { path: '/emblems', titleKey: 'modes.emblems.title' },
};

export const getOtherMode = (key: ModeKey) =>
  key === 'city-flags' ? MODE_LINKS.emblems : MODE_LINKS['city-flags'];
